/**
 * Plan 33 — Tipalti AP integration data layer.
 *
 * Exposes TanStack Query hooks for the Settings > My-Kitchen Tipalti
 * card:
 *   - status read (connected / not connected)
 *   - send onboarding invite (Tipalti emails the payee an onboarding link)
 *   - disconnect (no-op under the single-payer model)
 *
 * The ORDO_ADMIN force-resend surface lives in apps/customers per the
 * app-scope policy; the customer-facing client here only renders the
 * set-up / status controls.
 *
 * Server REST routes live at `/api/tipalti/payees/*` (see
 * `apps/ordo/server/src/routes/tipalti-payees.ts`). The vendor's Tipalti
 * internal payee id is the `billing.tipalti_id` org setting, set by ORDO_ADMIN.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — keep identical to the server response shapes.
// ---------------------------------------------------------------------------

export type TipaltiStatus = 'CONNECTED' | 'NOT_CONNECTED' | 'REAUTHORIZATION_REQUIRED' | 'MANUAL_BILLING'

export interface TipaltiStatusResponse {
  status: TipaltiStatus
  connectedAt: string | null
  lastRotatedAt: string | null
}

export interface InvitePayeeResponse {
  status: string
  sentTime: string | null
}

export interface DisconnectResponse {
  status: TipaltiStatus
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchTipaltiStatus(orgId: string, signal?: AbortSignal): Promise<TipaltiStatusResponse> {
  return apiFetch<TipaltiStatusResponse>(`/api/tipalti/payees/${encodeURIComponent(orgId)}/status`, {
    signal,
  })
}

export async function inviteTipaltiPayee(orgId: string): Promise<InvitePayeeResponse> {
  return apiFetch<InvitePayeeResponse>(`/api/tipalti/payees/${encodeURIComponent(orgId)}/invite`, {
    method: 'POST',
  })
}

export async function disconnectTipalti(orgId: string): Promise<DisconnectResponse> {
  return apiFetch<DisconnectResponse>(`/api/tipalti/payees/${encodeURIComponent(orgId)}/disconnect`, {
    method: 'POST',
  })
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const tipaltiStatusQueryKey = (orgId: string) => ['tipalti', 'status', orgId] as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useTipaltiStatusQuery(orgId: string | null) {
  return useQuery<TipaltiStatusResponse>({
    queryKey: tipaltiStatusQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => fetchTipaltiStatus(orgId!, signal),
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useInviteTipaltiPayeeMutation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: inviteTipaltiPayee,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tipaltiStatusQueryKey(orgId) })
    },
  })
}

export function useDisconnectTipaltiMutation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: disconnectTipalti,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tipaltiStatusQueryKey(orgId) })
    },
  })
}
